import { ThemePreset } from "../types/theme";
import { convertColorToHSL } from "./color-converter";

export function applyStyleToElement(
  element: HTMLElement,
  key: string,
  value: string
) {
  const currentStyle = element.getAttribute("style") || "";
  // Remove the existing variable definitions with the same name
  const cleanedStyle = currentStyle.replace(
    new RegExp(`--${key}:\\s*[^;]+;?`, "g"),
    ""
  ).trim();

  element.setAttribute(
    "style",
    `${cleanedStyle}--${key}: ${value};`
  );
}

export const generateThemeStyles = (
  preset: ThemePreset | undefined,
  mode: "light" | "dark",
  customStyles?: Record<string, string>
) => {
  if (!preset) return {};

  const baseStyles = preset.styles[mode];

  // Merge custom styles if provided
  const mergedStyles = { ...baseStyles, ...customStyles };

  const cssVariables: Record<string, string> = {};

  // List of color properties that need HSL conversion for Tailwind
  const colorProperties = [
    'background', 'foreground', 'card', 'card-foreground',
    'popover', 'popover-foreground', 'primary', 'primary-foreground',
    'secondary', 'secondary-foreground', 'muted', 'muted-foreground',
    'accent', 'accent-foreground', 'destructive', 'destructive-foreground',
    'border', 'input', 'ring', 'chart-1', 'chart-2', 'chart-3', 'chart-4', 'chart-5',
    'sidebar', 'sidebar-foreground', 'sidebar-primary', 'sidebar-primary-foreground',
    'sidebar-accent', 'sidebar-accent-foreground', 'sidebar-border', 'sidebar-ring'
  ];

  // Convert style object to CSS custom properties
  Object.entries(mergedStyles).forEach(([key, value]) => {
    const cssKey = key.replace(/([A-Z])/g, "-$1").toLowerCase();

    // Handle shadow properties specially to create shadow variants
    if (key.startsWith('shadow-')) {
      const shadowType = key.replace('shadow-', '');
      if (shadowType === 'color') {
        cssVariables[`--shadow-color`] = value;
      } else if (shadowType === 'opacity') {
        cssVariables[`--shadow-opacity`] = value;
      } else if (shadowType === 'blur') {
        cssVariables[`--shadow-blur`] = value;
      } else if (shadowType === 'spread') {
        cssVariables[`--shadow-spread`] = value;
      } else if (shadowType === 'offset-x') {
        cssVariables[`--shadow-offset-x`] = value;
      } else if (shadowType === 'offset-y') {
        cssVariables[`--shadow-offset-y`] = value;
      }
    } else {
      // Convert color values to HSL format for Tailwind compatibility
      if (colorProperties.includes(key) && typeof value === 'string') {
        cssVariables[`--${cssKey}`] = convertColorToHSL(value);
      } else {
        cssVariables[`--${cssKey}`] = value;
      }
    }
  });

  // Generate shadow variants
  const shadowColor = mergedStyles['shadow-color'] || 'hsl(0 0% 0%)';
  const shadowOpacity = mergedStyles['shadow-opacity'] || '0.1';
  const shadowBlur = mergedStyles['shadow-blur'] || '3px';
  const shadowSpread = mergedStyles['shadow-spread'] || '0px';
  const shadowOffsetX = mergedStyles['shadow-offset-x'] || '0px';
  const shadowOffsetY = mergedStyles['shadow-offset-y'] || '1px';

  // Always generate shadows, even if shadow-color is not explicitly set
  if (shadowColor) {
    // Extract HSL values without alpha if the color is already in HSL format
    let shadowColorHSL: string;
    if (shadowColor.startsWith('hsl(') && shadowColor.includes('/')) {
      // Color is already in HSL format with alpha, extract just the HSL part
      const hslMatch = shadowColor.match(/hsl\(([^\/]+)\/[^)]+\)/);
      shadowColorHSL = hslMatch ? hslMatch[1] : convertColorToHSL(shadowColor);
    } else {
      shadowColorHSL = convertColorToHSL(shadowColor);
    }

    // Generate different shadow sizes based on the theme with proper HSL alpha
    const blurValue = parseFloat(shadowBlur.replace('px', ''));
    const spreadValue = parseFloat(shadowSpread.replace('px', ''));

    cssVariables['--shadow-2xs'] = `${shadowOffsetX} ${shadowOffsetY} 1px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity) * 0.5})`;
    cssVariables['--shadow-xs'] = `${shadowOffsetX} ${shadowOffsetY} 2px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity) * 0.5})`;
    cssVariables['--shadow-sm'] = `${shadowOffsetX} ${shadowOffsetY} ${shadowBlur} ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 1px 2px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;
    cssVariables['--shadow'] = `${shadowOffsetX} ${shadowOffsetY} ${shadowBlur} ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 1px 2px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;
    cssVariables['--shadow-md'] = `${shadowOffsetX} ${shadowOffsetY} ${blurValue * 1.5}px ${spreadValue * 1.5}px hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 2px 4px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;
    cssVariables['--shadow-lg'] = `${shadowOffsetX} ${shadowOffsetY} ${blurValue * 2}px ${spreadValue * 2}px hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 4px 6px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;
    cssVariables['--shadow-xl'] = `${shadowOffsetX} ${shadowOffsetY} ${blurValue * 2.5}px ${spreadValue * 2.5}px hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 8px 10px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;
    cssVariables['--shadow-2xl'] = `${shadowOffsetX} ${shadowOffsetY} ${blurValue * 3}px ${spreadValue * 3}px hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)}), ${shadowOffsetX} 10px 15px ${shadowSpread} hsl(${shadowColorHSL} / ${parseFloat(shadowOpacity)})`;

    console.log('🎨 Shadow Debug - Generated shadows:', {
      shadowColor,
      shadowColorHSL,
      shadowOffsetX,
      shadowOffsetY,
      shadowBlur,
      shadowSpread,
      shadowOpacity,
      generatedShadow: cssVariables['--shadow']
    });
  }

  return cssVariables;
};

export const applyThemeToDocument = (
  preset: ThemePreset | undefined,
  mode: "light" | "dark",
  customStyles?: Record<string, string>
) => {
  console.log('🎨 Theme Debug - Applying theme:', {
    presetName: preset?.label,
    mode,
    hasShadowColor: preset?.styles[mode]?.['shadow-color'],
    shadowOffsetX: preset?.styles[mode]?.['shadow-offset-x'],
    shadowOffsetY: preset?.styles[mode]?.['shadow-offset-y'],
    customStyles
  });

  const cssVariables = generateThemeStyles(preset, mode, customStyles);

  const root = document.documentElement;
  const body = document.body;

  // Apply CSS variables to :root
  Object.entries(cssVariables).forEach(([property, value]) => {
    const key = property.replace('--', '');
    applyStyleToElement(root, key, value);
  });

  // Ensure background color is applied directly to html and body
  if (cssVariables['--background']) {
    // Convert HSL back to full HSL format for direct application
    const bgValue = cssVariables['--background'];
    if (bgValue && !bgValue.includes('hsl(') && !bgValue.includes('rgb(') && !bgValue.startsWith('#')) {
      // It's in Tailwind HSL format "h s% l%", convert to full HSL
      root.style.backgroundColor = `hsl(${bgValue})`;
      body.style.backgroundColor = `hsl(${bgValue})`;
    } else {
      // Already in a format that can be used directly
      root.style.backgroundColor = bgValue;
      body.style.backgroundColor = bgValue;
    }
  }

  // Update data-theme attribute for Tailwind CSS
  root.setAttribute("data-theme", mode);
};

export const getThemeColors = (
  preset: ThemePreset | undefined,
  mode: "light" | "dark"
) => {
  if (!preset) return {};

  const styles = preset.styles[mode];

  return {
    primary: styles.primary,
    secondary: styles.secondary,
    accent: styles.accent,
    background: styles.background,
    foreground: styles.foreground,
    muted: styles.muted,
    "muted-foreground": styles["muted-foreground"],
    border: styles.border,
    input: styles.input,
    ring: styles.ring,
    destructive: styles.destructive,
    "destructive-foreground": styles["destructive-foreground"],
  };
};

export const getChartColors = (
  preset: ThemePreset | undefined,
  mode: "light" | "dark"
) => {
  if (!preset) return [];

  const styles = preset.styles[mode];

  return [
    styles["chart-1"] || styles.primary,
    styles["chart-2"] || styles.secondary,
    styles["chart-3"] || styles.accent,
    styles["chart-4"] || styles.primary,
    styles["chart-5"] || styles.secondary,
  ];
};

export const createThemePreview = (
  preset: ThemePreset | undefined,
  mode: "light" | "dark"
) => {
  if (!preset) return {};

  const styles = preset.styles[mode];

  return {
    backgroundColor: styles.background,
    color: styles.foreground,
    borderColor: styles.border,
    primaryColor: styles.primary,
    secondaryColor: styles.secondary,
    accentColor: styles.accent,
  };
};

